/**
 * KERN Feedback Widget - Minimal Additional Styles
 *
 * These styles only add widget-specific positioning and custom components.
 * All standard components use official KERN CSS classes.
 */

// Base URL for widget assets - derived from the script location
function getWidgetBaseUrl(): string {
  const scripts = document.querySelectorAll('script[src*="feedback-widget"]');
  if (scripts.length > 0) {
    const scriptSrc = (scripts[scripts.length - 1] as HTMLScriptElement).src;
    return scriptSrc.substring(0, scriptSrc.lastIndexOf('/') + 1);
  }
  return 'https://feedback.kern-ux.de/widget/';
}

/**
 * Detect whether the host page already ships the KERN design system.
 *
 * The widget bundles its own copy of `kern.min.css`, but injecting that into a
 * page which ALREADY loads KERN would redefine every `.kern-*` class and KERN
 * design token globally — silently overriding the host's (possibly different)
 * KERN version and risking visual breakage of the surrounding application.
 *
 * To stay a good citizen the widget therefore only injects its bundled KERN
 * when the host does not already provide it. Detection is deliberately
 * conservative (it prefers NOT injecting):
 *   1. A core KERN custom property already resolves on the document root.
 *   2. A stylesheet whose URL references KERN (excluding our own bundle).
 */
function hostHasKernStyles(): boolean {
  try {
    if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
      const rootStyles = window.getComputedStyle(document.documentElement);
      const kernTokens = [
        '--kern-color-action-default',
        '--kern-color-layout-background-default',
        '--kern-color-primary',
      ];
      for (const token of kernTokens) {
        if (rootStyles.getPropertyValue(token).trim() !== '') {
          return true;
        }
      }
    }

    const links = document.querySelectorAll('link[rel~="stylesheet"][href]');
    for (const link of Array.from(links)) {
      // Never mistake our own injected bundle for the host's KERN.
      if (link.id === 'kern-feedback-kern-css' || link.id === 'kern-feedback-font-css') {
        continue;
      }
      const href = (link.getAttribute('href') || '').toLowerCase();
      if (href.includes('kern') && !href.includes('feedback')) {
        return true;
      }
    }
  } catch {
    // getComputedStyle / DOM query unavailable (SSR, jsdom) — assume absent.
  }
  return false;
}

// Load external KERN CSS + fonts from the widget server, but ONLY when the
// host page does not already provide KERN. This guarantees the widget never
// overrides an existing KERN design system on the host page.
function loadExternalStyles(): void {
  if (hostHasKernStyles()) {
    return;
  }

  const baseUrl = getWidgetBaseUrl();

  if (!document.getElementById('kern-feedback-kern-css')) {
    const kernCss = document.createElement('link');
    kernCss.rel = 'stylesheet';
    kernCss.href = `${baseUrl}kern.min.css`;
    kernCss.id = 'kern-feedback-kern-css';
    document.head.appendChild(kernCss);
  }

  if (!document.getElementById('kern-feedback-font-css')) {
    const fontCss = document.createElement('link');
    fontCss.rel = 'stylesheet';
    fontCss.href = `${baseUrl}fonts/fira-sans.css`;
    fontCss.id = 'kern-feedback-font-css';
    document.head.appendChild(fontCss);
  }
}

export const WIDGET_STYLES = `
/* ============================================
   KERN Feedback Widget - Minimal Custom Styles
   Only positioning and widget-specific elements
   ============================================ */

/* Widget Container */
.kern-feedback-widget {
  --kern-z-index: 999999;
}

/* Textarea - vertical resize only */
.kern-feedback-widget textarea,
.kern-feedback-widget .kern-form-input__input[id="kern-feedback-comment"] {
  resize: vertical !important;
  max-width: 100% !important;
  width: 100% !important;
}

/* Force the text I-beam cursor on every text-like input inside the widget.
   KERN's own CSS leaves the cursor unset (so the browser default applies),
   but if the host page sets cursor: pointer on a wrapping element — common
   on portals or design-system shells — the textarea/input inherits that
   visually. Explicitly setting cursor: text here keeps the widget's form
   behavior identical to the standalone KERN markup regardless of host CSS.
   The KERN disabled / readonly rules (cursor: not-allowed) take precedence
   because their selectors are more specific and KERN ships them with
   higher-priority qualifiers. */
.kern-feedback-widget textarea,
.kern-feedback-widget input[type='text'],
.kern-feedback-widget input[type='email'],
.kern-feedback-widget input[type='url'],
.kern-feedback-widget input[type='search'],
.kern-feedback-widget input[type='tel'],
.kern-feedback-widget input[type='number'],
.kern-feedback-widget input[type='password'],
.kern-feedback-widget input:not([type]) {
  cursor: text;
}

/* Floating Trigger Button - Position only */
.kern-feedback-trigger {
  position: fixed !important;
  z-index: var(--kern-z-index) !important;
  border-radius: 8px 8px 0 0 !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
  white-space: nowrap !important;
}

.kern-feedback-trigger__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25em;
  height: 1.25em;
}

.kern-feedback-trigger__icon svg path {
  stroke: currentColor;
}

.kern-feedback-trigger--bottom-right {
  right: 0;
  bottom: 50%;
  transform: rotate(-90deg) translateX(50%);
  transform-origin: right bottom;
}

.kern-feedback-trigger--bottom-left {
  left: 0;
  bottom: 50%;
  transform: rotate(90deg) translateX(-50%);
  transform-origin: left bottom;
}

.kern-feedback-trigger--right-center {
  right: 0;
  top: 50%;
  transform: rotate(-90deg) translateY(-100%);
  transform-origin: right top;
}

/* ----------------------------------------
   Desktop-only: swipe-in entrance + outline
   ----------------------------------------
   The floating trigger is hidden below 575px (see the mobile media query),
   where the inline .kern-feedback-trigger-mobile symbol is used instead.
   Both the entrance animation and the accent outline are therefore scoped to
   the non-mobile viewport so mobile is untouched.

   The keyframes keep the position-specific rotation/translation of each
   modifier and merely PREPEND a screen-space translateX. Because transform
   functions apply right-to-left, the leading translateX moves the already
   rotated button along the viewport's X axis — off the right edge at 0% and
   back to its resting place at 100%. The animation is ease-out, so the
   button decelerates into its final position. */
@media (min-width: 575px) {
  .kern-feedback-trigger {
    outline: 2px solid #0099ff !important;
    outline-offset: 0 !important;
  }

  /* Keep a distinguishable keyboard focus indicator despite the permanent
     accent outline: offset it so it reads as a separate ring. */
  .kern-feedback-trigger:focus-visible {
    outline: 2px solid #0099ff !important;
    outline-offset: 3px !important;
  }

  .kern-feedback-trigger--bottom-right {
    animation: kern-feedback-trigger-swipe-in-bottom-right 500ms ease-out both;
  }

  .kern-feedback-trigger--right-center {
    animation: kern-feedback-trigger-swipe-in-right-center 500ms ease-out both;
  }

  /* Left-anchored variant mirrors the motion and enters from the near edge,
     otherwise it would fly across the whole viewport. */
  .kern-feedback-trigger--bottom-left {
    animation: kern-feedback-trigger-swipe-in-bottom-left 500ms ease-out both;
  }
}

@keyframes kern-feedback-trigger-swipe-in-bottom-right {
  from {
    opacity: 0;
    transform: translateX(150%) rotate(-90deg) translateX(50%);
  }
  to {
    opacity: 1;
    transform: translateX(0) rotate(-90deg) translateX(50%);
  }
}

@keyframes kern-feedback-trigger-swipe-in-right-center {
  from {
    opacity: 0;
    transform: translateX(150%) rotate(-90deg) translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0) rotate(-90deg) translateY(-100%);
  }
}

@keyframes kern-feedback-trigger-swipe-in-bottom-left {
  from {
    opacity: 0;
    transform: translateX(-150%) rotate(90deg) translateX(-50%);
  }
  to {
    opacity: 1;
    transform: translateX(0) rotate(90deg) translateX(-50%);
  }
}

/* Respect the OS "reduce motion" setting: show the button in place. */
@media (prefers-reduced-motion: reduce) {
  .kern-feedback-trigger--bottom-right,
  .kern-feedback-trigger--bottom-left,
  .kern-feedback-trigger--right-center {
    animation: none !important;
  }
}

/* Backdrop */
.kern-feedback-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: calc(var(--kern-z-index) + 1);
  opacity: 0;
  visibility: hidden;
  transition: opacity 150ms ease, visibility 150ms ease;
}

.kern-feedback-backdrop--visible {
  opacity: 1;
  visibility: visible;
}

/* Dialog - Desktop: centered modal */
.kern-feedback-widget .kern-dialog {
  position: fixed !important;
  z-index: calc(var(--kern-z-index) + 2) !important;
  margin: 0 !important;
  border: none !important;
  padding: 0 !important;
  opacity: 0;
  visibility: hidden;
  transition: opacity 150ms ease, transform 150ms ease, visibility 150ms ease;
  display: -webkit-box !important;
  display: -webkit-flex !important;
  display: flex !important;
  -webkit-box-orient: vertical !important;
  -webkit-box-direction: normal !important;
  -webkit-flex-direction: column !important;
  flex-direction: column !important;
  -webkit-box-sizing: border-box !important;
  box-sizing: border-box !important;
  /* Desktop: centered */
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) scale(0.95);
  -webkit-transform: translate(-50%, -50%) scale(0.95);
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow: visible !important;
  background: var(--kern-color-layout-background-default-contextual, #FFFFFF) !important;
  border-radius: 4px !important;
  -webkit-box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15) !important;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15) !important;
  /* Force Safari to respect boundaries */
  -webkit-backface-visibility: hidden !important;
  backface-visibility: hidden !important;
}

.kern-feedback-widget .kern-dialog--visible {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, -50%) scale(1);
  -webkit-transform: translate(-50%, -50%) scale(1);
}

/* Desktop dialog border: the modal is a floating surface over page content,
   so add a grey border to delineate its dimensions and improve visibility.
   Uses KERN's layout-border token (a mid-grey that reads on both light and
   dark surfaces): darker/grey against the white light-mode dialog, and a
   lighter grey against the near-black dark-mode dialog.
   Scoped to desktop (min-width: 575px) because the mobile dialog is a
   full-screen, edge-to-edge overlay that needs no outline. */
@media (min-width: 575px) {
  .kern-feedback-widget .kern-dialog {
    border: 1px solid var(--kern-color-layout-border-contextual, #6E7597) !important;
  }
}

.kern-feedback-widget .kern-dialog__header {
  -webkit-box-flex: 0 !important;
  -webkit-flex: 0 0 auto !important;
  flex: 0 0 auto !important;
  -webkit-box-sizing: border-box !important;
  box-sizing: border-box !important;
  padding: 1rem !important;
  /* Force the header to share the dialog's themed surface. Some KERN
     stylesheet versions give .kern-dialog__header its own light background,
     which would otherwise render a white header (invisible title) in dark
     mode. */
  background: var(--kern-color-layout-background-default-contextual, #FFFFFF) !important;
}

/* Keep the dialog title readable on the themed surface: light text in dark
   mode, dark text in light mode. Guards against KERN versions that leave
   .kern-title uncolored inside the widget's themed container. */
.kern-feedback-widget .kern-dialog__header .kern-title {
  color: var(--kern-color-layout-text-default-contextual, #131525) !important;
}

.kern-feedback-widget .kern-dialog__body {
  overflow-y: auto !important;
  -webkit-box-flex: 1 !important;
  -webkit-flex: 1 1 auto !important;
  flex: 1 1 auto !important;
  min-height: 0 !important;
  -webkit-box-sizing: border-box !important;
  box-sizing: border-box !important;
  padding: 0 1rem 1rem 1rem !important;
  background: var(--kern-color-layout-background-default-contextual, #FFFFFF) !important;
}

.kern-feedback-widget .kern-dialog__footer {
  -webkit-box-flex: 0 !important;
  -webkit-flex: 0 0 auto !important;
  flex: 0 0 auto !important;
  -webkit-flex-shrink: 0 !important;
  flex-shrink: 0 !important;
  -webkit-flex-grow: 0 !important;
  flex-grow: 0 !important;
  display: -webkit-box !important;
  display: -webkit-flex !important;
  display: flex !important;
  gap: 0.5rem !important;
  -webkit-box-sizing: border-box !important;
  box-sizing: border-box !important;
  padding: 1rem !important;
  padding-top: 2.0rem !important;
  padding-bottom: 1.35rem !important; /* Extra padding for Safari */
  background: var(--kern-color-layout-background-default-contextual, #FFFFFF) !important;
  border-top: 1px solid var(--kern-color-layout-border-contextual, #E5E7EB) !important;
  /* Ensure footer is always visible */
  position: relative !important;
  z-index: 2147483650 !important;
}

/* Star Rating - Widget-specific component */
.kern-feedback-rating {
  display: flex;
  gap: 0.25rem;
  align-items: center;
  flex-wrap: wrap;
}

.kern-feedback-rating__star {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--kern-color-gray-300, #D1D5DB);
  transition: color 150ms ease, transform 150ms ease;
}

.kern-feedback-rating__star:hover {
  transform: scale(1.1);
}

.kern-feedback-rating__star:focus-visible {
  outline: 2px solid var(--kern-color-primary, #004B76);
  outline-offset: 2px;
}

.kern-feedback-rating__star svg {
  width: 1.75rem;
  height: 1.75rem;
}

.kern-feedback-rating__star--active,
.kern-feedback-rating__star--hover {
  color: #FBBF24;
}

.kern-feedback-rating__label {
  margin-left: 0.5rem;
}

/* Screenshot Preview - Widget-specific */
.kern-feedback-screenshot {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  /* KERN's .kern-form-input applies align-items: flex-start which would
     collapse this inner column to the width of its widest child. Stretching
     ensures the capture button (kern-btn--block) and the file upload below
     can fully span the dialog width. */
  align-self: stretch;
  width: 100%;
}

/* Desktop file-upload alternative wrapper sits inside .kern-feedback-screenshot.
   It is itself a .kern-form-input flex column, so it needs the same stretch fix. */
.kern-feedback-screenshot .kern-feedback-file-upload {
  align-self: stretch;
  width: 100%;
}

/* The native file input has an intrinsic width and would otherwise be much
   narrower than the dialog. Force it to span the full available width. */
.kern-feedback-widget #kern-feedback-file-upload {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

.kern-feedback-screenshot__preview {
  position: relative;
  max-width: 200px;
  border-radius: 4px;
  overflow: hidden;
}

.kern-feedback-screenshot__preview img {
  display: block;
  width: 100%;
  height: auto;
}

.kern-feedback-screenshot__remove {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
}

.kern-feedback-screenshot__remove:hover {
  background: var(--kern-color-danger, #DC2626);
}

.kern-feedback-screenshot__remove svg {
  width: 0.875rem;
  height: 0.875rem;
}

/* Success State - Uses KERN alert pattern */
.kern-feedback-success {
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  flex: 1;
}

.kern-feedback-success--visible {
  display: flex !important;
}

/* Hide form and footer when success is visible - scoped to widget.
   Every selector below is scoped to .kern-feedback-widget so the widget never
   emits a rule that could match a KERN element on the host page. */
.kern-feedback-widget .kern-dialog__header--hidden {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  overflow: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
}

.kern-feedback-widget .kern-dialog__body--hidden {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  overflow: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
}

.kern-feedback-widget .kern-dialog__footer--hidden {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  overflow: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Hide spacers when success is visible */
.kern-feedback-widget .kern-feedback-spacer--hidden {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}

.kern-feedback-success__icon {
  width: 3rem;
  height: 3rem;
  margin-bottom: 1rem;
  color: var(--kern-color-success, #059669);
}

.kern-feedback-success__icon svg {
  width: 100%;
  height: 100%;
}

/* Inline loader for buttons */
.kern-feedback-loader {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: kern-feedback-spin 0.75s linear infinite;
  vertical-align: middle;
  margin-right: 0.5rem;
}

/* Small loader variant for inline status */
.kern-feedback-loader--small {
  width: 0.75rem;
  height: 0.75rem;
  border-width: 1.5px;
  margin-right: 0.375rem;
}

/* Screenshot status indicator */
.kern-feedback-screenshot__status {
  display: block;
  margin-top: 0.5rem;
}

/* Desktop: separator between "capture" and "upload" alternatives */
.kern-feedback-screenshot__separator {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.25rem 0;
  color: var(--kern-color-layout-text-muted-contextual, #6B7280);
  text-align: center;
}

.kern-feedback-screenshot__separator::before,
.kern-feedback-screenshot__separator::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--kern-color-layout-border-contextual, #E5E7EB);
}

.kern-feedback-screenshot__separator span {
  font-size: 0.875rem;
  white-space: nowrap;
}

/* Desktop: file upload alternative wrapper */
.kern-feedback-screenshot .kern-feedback-file-upload {
  margin: 0;
}

.kern-feedback-screenshot .kern-feedback-file-upload .kern-hint {
  font-size: 12px;
  margin-top: 0;
  color: var(--kern-color-layout-text-muted-contextual, #6B7280);
}

/* Fix vertical alignment of text in screenshot button and badge */
.kern-feedback-screenshot .kern-btn .kern-label,
.kern-feedback-screenshot .kern-badge .kern-label {
  position: relative;
  top: 4px;
}

/* Screenshot progress container - uses KERN progress component */
.kern-feedback-screenshot__progress {
  margin-top: 0.5rem;
}

.kern-feedback-screenshot__progress .kern-progress {
  width: 100%;
}

.kern-feedback-screenshot__progress .kern-label {
  margin-bottom: 0.25rem;
}

@keyframes kern-feedback-spin {
  to { transform: rotate(360deg); }
}

/* ============================================
   Mobile & Tablet: Full-screen overlay
   Breakpoint at 574px for mobile devices
   ============================================ */
@media (max-width: 574px) {
  /* Force full viewport coverage on mobile/tablet */
  .kern-feedback-widget .kern-dialog {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    width: 100vw !important;
    max-width: 100% !important;
    max-width: 100vw !important;
    min-width: 100% !important;
    min-width: 100vw !important;
    /* Safari-compatible height with fallbacks */
    height: 100% !important;
    height: 100vh !important;
    height: -webkit-fill-available !important;
    max-height: 100% !important;
    max-height: 100vh !important;
    max-height: -webkit-fill-available !important;
    min-height: 100% !important;
    min-height: 100vh !important;
    min-height: -webkit-fill-available !important;
    border-radius: 0 !important;
    transform: translateY(100%) !important;
    -webkit-transform: translateY(100%) !important;
    margin: 0 !important;
    padding: 0 !important;
    display: -webkit-box !important;
    display: -webkit-flex !important;
    display: flex !important;
    -webkit-box-orient: vertical !important;
    -webkit-box-direction: normal !important;
    -webkit-flex-direction: column !important;
    flex-direction: column !important;
    -webkit-box-sizing: border-box !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    inset: 0 !important;
    border: none !important;
    background: var(--kern-color-layout-background-default-contextual, #FFFFFF) !important;
    z-index: 2147483650 !important;
  }

  .kern-feedback-widget .kern-dialog--visible {
    transform: translateY(0) !important;
    -webkit-transform: translateY(0) !important;
  }

  .kern-feedback-widget .kern-dialog__header {
    -webkit-box-flex: 0 !important;
    -webkit-flex: 0 0 auto !important;
    flex: 0 0 auto !important;
    padding: 1rem !important;
    -webkit-box-sizing: border-box !important;
    box-sizing: border-box !important;
    background: var(--kern-color-layout-background-default-contextual, #FFFFFF) !important;
  }

  .kern-feedback-widget .kern-dialog__body {
    -webkit-box-flex: 1 !important;
    -webkit-flex: 1 1 auto !important;
    flex: 1 1 auto !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    -webkit-overflow-scrolling: touch;
    min-height: 0 !important;
    padding: 1rem !important;
    -webkit-box-sizing: border-box !important;
    box-sizing: border-box !important;
    background: var(--kern-color-layout-background-default-contextual, #FFFFFF) !important;
  }

  .kern-feedback-widget .kern-dialog__footer {
    -webkit-box-flex: 0 !important;
    -webkit-flex: 0 0 auto !important;
    flex: 0 0 auto !important;
    -webkit-flex-grow: 0 !important;
    flex-grow: 0 !important;
    -webkit-flex-shrink: 0 !important;
    flex-shrink: 0 !important;
    padding: 1rem !important;
    border-top: 1px solid var(--kern-color-layout-border-contextual, #E5E7EB) !important;
    background: var(--kern-color-layout-background-default-contextual, #FFFFFF) !important;
    display: -webkit-box !important;
    display: -webkit-flex !important;
    display: flex !important;
    gap: 0.5rem !important;
    -webkit-box-sizing: border-box !important;
    box-sizing: border-box !important;
    min-height: auto !important;
    max-height: 5rem !important;
    height: auto !important;
  }

  .kern-feedback-widget .kern-dialog__footer .kern-btn {
    -webkit-box-flex: 1 !important;
    -webkit-flex: 1 1 auto !important;
    flex: 1 1 auto !important;
    height: auto !important;
    min-height: 2.75rem !important;
    max-height: 3rem !important;
    -webkit-align-self: center !important;
    align-self: center !important;
  }

  /* Success state fills the dialog on mobile */
  .kern-feedback-widget .kern-feedback-success {
    -webkit-box-flex: 1 !important;
    -webkit-flex: 1 1 auto !important;
    flex: 1 1 auto !important;
    -webkit-box-pack: center !important;
    -webkit-justify-content: center !important;
    justify-content: center !important;
    -webkit-box-align: center !important;
    -webkit-align-items: center !important;
    align-items: center !important;
    padding: 2rem !important;
    background: var(--kern-color-layout-background-default-contextual, #FFFFFF) !important;
  }

  .kern-feedback-trigger--right-center {
    display: none;
  }

  /* Hide the regular floating trigger on mobile */
  .kern-feedback-trigger {
    display: none !important;
  }

  /* Show mobile trigger in header area */
  .kern-feedback-trigger-mobile {
    display: flex !important;
  }
}

/* ============================================
   Mobile Trigger Button - Compact inline header icon
   Rendered INLINE (in normal flow) inside a host-provided container
   (config.triggerContainer, e.g. the site header) via the --inline modifier,
   so it becomes part of the header layout with NO fixed positioning and cannot
   overlap or shift the host page. Without a container it falls back to a
   self-contained fixed icon in the lower-right corner (--fallback).
   Renders as the bare KERN symbol only: no tinted circle, no background —
   just the glyph coloured with the adaptive KERN action token so it reads
   correctly in both light and dark mode.
   ============================================ */
.kern-feedback-trigger-mobile {
  display: none; /* Hidden by default, shown only on mobile via media query */
  width: 40px !important;
  height: 40px !important;
  padding: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  background: none !important;
  background-color: transparent !important;
  box-shadow: none !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  /* KERN symbol colour. The contextual action token adapts automatically
     between light and dark mode; the hex is a KERN dark-blue fallback for
     when the KERN tokens are not (yet) available. */
  color: var(--kern-color-action-default-contextual, #1e4e9c) !important;
  transition: transform 150ms ease, opacity 150ms ease !important;
}

/* Inline mode: sits in the normal flow of the host container (header).
   position: relative keeps it in-flow while providing a positioning context
   for its (suppressed) tooltip. */
.kern-feedback-trigger-mobile--inline {
  position: relative !important;
  top: auto !important;
  right: auto !important;
  bottom: auto !important;
  left: auto !important;
}

/* Fallback mode: no host container was provided, so pin a self-contained icon
   to the lower-right corner. This never participates in the host's header
   layout, so it cannot cause relative-positioning problems there. */
.kern-feedback-trigger-mobile--fallback {
  position: fixed !important;
  top: auto !important;
  left: auto !important;
  right: 1rem !important;
  bottom: 1rem !important;
  z-index: var(--kern-z-index, 999999) !important;
}

.kern-feedback-trigger-mobile:hover {
  transform: scale(1.08) !important;
  opacity: 0.85 !important;
}

.kern-feedback-trigger-mobile:focus-visible {
  outline: 2px solid var(--kern-color-action-default-contextual, #1e4e9c) !important;
  outline-offset: 2px !important;
  border-radius: 4px !important;
}

/* Dark mode: keep the bare KERN symbol but let the contextual action token
   flip to the lighter dark-mode blue so the glyph stays visible on a dark
   header. The theme is carried on the button itself (data-kern-theme) so it
   works whether the trigger is mounted inside container or in a host header.
   Falls back to a light blue when the KERN tokens are unavailable. */
.kern-feedback-trigger-mobile[data-kern-theme='dark'] {
  color: var(--kern-color-action-default-contextual, #8fb8f0) !important;
}

.kern-feedback-trigger-mobile[data-kern-theme='dark']:focus-visible {
  outline-color: var(--kern-color-action-default-contextual, #8fb8f0) !important;
}

/* Glyph size matches the KERN Chatbot widget's inline header launchers
   (ai-chat + full-text search). Those render a bare KERN icon element, which
   KERN sizes with the default dimension token (24px). Using the very same
   token here — with KERN's own 24px fallback — keeps all three action icons
   optically identical when they sit next to each other in a host header. */
.kern-feedback-trigger-mobile__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--kern-metric-dimension-default, 24px);
  height: var(--kern-metric-dimension-default, 24px);
}

.kern-feedback-trigger-mobile__icon svg {
  width: 100%;
  height: 100%;
  fill: currentColor;
}

/* ============================================
   Tooltips for icon-only action buttons

   Visual design follows the KERN Chatbot widget: an INVERTED bubble (dark on
   light pages, light on dark pages) built from the KERN inverted layout
   tokens, small radius, no arrow, opacity fade.

   Positioning deliberately differs from the chatbot's absolutely-positioned
   bubble: the widget keeps ONE shared bubble inside its own body-level
   container and places it with position: fixed. A bubble anchored inside the
   host's header would otherwise be clipped by common overflow: hidden /
   contain rules — which is exactly why the previous tooltip had to be
   suppressed for the inline trigger — or trapped by a transformed ancestor.
   Same reasoning as for the dialog and the floating button.
   ============================================ */
.kern-feedback-tooltip {
  position: fixed !important;
  top: 0;
  left: 0;
  z-index: calc(var(--kern-z-index, 999999) + 2) !important;
  margin: 0 !important;
  padding: 0.25rem 0.5rem !important;
  border: none !important;
  border-radius: 0.25rem !important;
  font-size: 0.875rem !important;
  line-height: 1.4 !important;
  font-weight: 400 !important;
  white-space: nowrap !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  background: var(--kern-color-layout-background-inverted, #1c1c1c) !important;
  color: var(--kern-color-layout-text-inverse, #ffffff) !important;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 150ms ease, visibility 150ms ease;
}

.kern-feedback-tooltip--visible {
  opacity: 1;
  visibility: visible;
}

/* Dark host pages: the KERN inverted tokens flip on their own, but the literal
   fallbacks (used when the host does not ship KERN) must flip too, otherwise a
   dark bubble would sit on a dark surface. */
.kern-feedback-tooltip[data-kern-theme='dark'] {
  background: var(--kern-color-layout-background-inverted, #f2f2f2) !important;
  color: var(--kern-color-layout-text-inverse, #1c1c1c) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5) !important;
}
`;

export function injectStyles(): void {
  if (document.getElementById('kern-feedback-styles')) {
    return;
  }

  // Load external KERN CSS and fonts from the widget server
  loadExternalStyles();

  const style = document.createElement('style');
  style.id = 'kern-feedback-styles';
  style.textContent = WIDGET_STYLES;
  document.head.appendChild(style);
}
